import { useState, useEffect } from 'react';
import type { Order } from '../types/order';
import type { ProgressReport } from '../types/lesson';
import { getProgressReportByOrderId } from '../services/progressReports';
import { useAuth } from '../contexts/AuthContext';
import BackButton from './BackButton';
import { capName } from '../utils/capName';

interface PlayerProgressReportProps {
  order: Order;
  onBack: () => void;
  /** Only passed (and shown) when the order is not autoRenewOptedIn and not isRecurringPlan. */
  onWhatsNext?: () => void;
  /** Disable "What's next" when the player already has an active training program with this coach */
  hasActiveProgram?: boolean;
}

function formatSentDate(iso?: string): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

export default function PlayerProgressReport({ order, onBack, onWhatsNext, hasActiveProgram }: PlayerProgressReportProps) {
  const [report, setReport] = useState<ProgressReport | null>(null);
  const [loading, setLoading] = useState(true);
  const { currentUser } = useAuth();

  const coachFirstName = capName(order.coachName.split(' ')[0]);
  const coachAvatarUrl = order.coachAvatarUrl;
  // Viewer-role check: both coach and player can land on this screen via
  // the chat card. The rebook bridge is player-only — the coach has no
  // need to "keep building" with themselves.
  const viewerIsCoach = !!currentUser && currentUser.uid === order.coachId;

  useEffect(() => {
    getProgressReportByOrderId(order.id)
      .then(r => { setReport(r); setLoading(false); })
      .catch(() => setLoading(false));
  }, [order.id]);

  const sentDate = formatSentDate(report?.createdAt);

  return (
    <div className="min-h-full bg-db-bg">
      <div className="p-6 md:p-8 pt-5 md:pt-8 max-w-[760px] w-full mx-auto">

        <BackButton onClick={onBack} fallback="/lessons" size="sm" flush className="mb-6" sticky />

        {/* LETTER — one unified container holding letterhead, body, signature. */}
        <div
          className="bg-white border border-gray-200 rounded-xl shadow-lg mb-5 px-7 py-10 md:px-12 md:py-14"
        >
          {/* LETTERHEAD */}
          <div className="text-[11px] font-semibold uppercase tracking-[.14em] text-gray-500 mb-3">
            Training Program · Progress Report
          </div>
          <h1 className="text-[28px] md:text-[32px] font-medium text-db-text-1 tracking-tight leading-tight mb-5">
            {coachFirstName}'s report for you
          </h1>
          <div className="flex items-center gap-2.5">
            <div className="w-8 aspect-[4/5] rounded-[8px] overflow-hidden bg-db-navy-light flex-shrink-0 flex items-center justify-center">
              {coachAvatarUrl ? (
                <img src={coachAvatarUrl} alt={coachFirstName} className="w-full h-full object-cover" />
              ) : (
                <span className="text-[12px] font-bold text-biscayne">{coachFirstName.charAt(0)}</span>
              )}
            </div>
            <div className="text-[14px] text-gray-500">
              From {coachFirstName}{sentDate ? ` · ${sentDate}` : ''}
            </div>
          </div>

          {/* Teal masthead rule — short, left-aligned. */}
          <div className="my-8 h-px w-10 bg-biscayne" aria-hidden="true" />

          {/* BODY */}
          {loading ? (
            <div className="py-6 text-[13px] text-db-text-2">Loading report…</div>
          ) : !report ? (
            <div className="py-6 text-[13px] text-db-text-2">Report not available yet.</div>
          ) : (
            <>
              <LetterSection label="What improved most" body={report.whatImproved} />

              <div className="my-7 md:my-8 border-t border-gray-200" />

              <LetterSection label="What you should keep doing" body={report.keepDoing} />

              <div className="my-7 md:my-8 border-t border-gray-200" />

              <LetterSection label="What's next" body={report.nextFocus} />

              {report.personalNote && report.personalNote.trim().length > 0 && (
                <>
                  <div className="my-7 md:my-8 border-t border-gray-200" />
                  <LetterSection label="My thoughts" body={report.personalNote} />
                </>
              )}

              {/* Signature — em-dash + first name only, no avatar. */}
              <div className="mt-10 md:mt-12 text-base text-db-text-1">— {coachFirstName}</div>
            </>
          )}
        </div>

        {/* Bottom — Bridge to rebook. Player-only; coaches see only the letter. */}
        {report && !viewerIsCoach && (
          <>
            {order.autoRenewOptedIn ? (
              <div className="bg-white rounded-xl px-6 py-5 border border-db-border flex items-center gap-3">
                <div className="w-8 h-8 rounded-full bg-green-50 flex items-center justify-center flex-shrink-0">
                  <svg width="14" height="14" fill="none" viewBox="0 0 24 24" stroke="#16a34a" strokeWidth="2.5">
                    <polyline points="20 6 9 17 4 12" />
                  </svg>
                </div>
                <div>
                  <div className="text-[14px] font-bold text-db-text-1">Next program already set up</div>
                  <div className="text-[12px] text-db-text-2">Your next Training Program with {coachFirstName} will appear in Lessons once this one wraps up.</div>
                </div>
              </div>
            ) : order.isRecurringPlan ? (
              <div className="bg-white rounded-xl px-6 py-5 border border-db-border flex items-center gap-3">
                <div className="w-8 h-8 rounded-full bg-blue-50 flex items-center justify-center flex-shrink-0">
                  <svg width="14" height="14" fill="none" viewBox="0 0 24 24" stroke="#0284C7" strokeWidth="2.5">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                  </svg>
                </div>
                <div>
                  <div className="text-[14px] font-bold text-db-text-1">Recurring plan active</div>
                  <div className="text-[12px] text-db-text-2">Your next program with {coachFirstName} will start automatically. No action needed.</div>
                </div>
              </div>
            ) : onWhatsNext ? (
              <div className="bg-db-navy-light rounded-xl flex items-center justify-between gap-4" style={{ padding: '24px 28px' }}>
                <div className="min-w-0">
                  <div className="text-[16px] font-bold text-db-text-1 mb-1">Ready to keep going?</div>
                  <div className="text-[13px] text-db-text-2 leading-relaxed">
                    Keep building on what you've started with {coachFirstName}.
                  </div>
                </div>
                <button
                  onClick={onWhatsNext}
                  disabled={hasActiveProgram}
                  title={hasActiveProgram ? 'You already have an active training program with this coach' : undefined}
                  className="bg-biscayne hover:bg-db-navy-hover text-white text-[13px] font-bold px-4 py-2.5 rounded-xl whitespace-nowrap disabled:opacity-40 disabled:cursor-not-allowed transition-colors flex-shrink-0 btn-press"
                >
                  What's next →
                </button>
              </div>
            ) : null}
          </>
        )}

      </div>
    </div>
  );
}

interface LetterSectionProps {
  label: string;
  body: string;
}

function LetterSection({ label, body }: LetterSectionProps) {
  return (
    <div>
      <div className="text-[11px] font-semibold uppercase tracking-[.14em] text-biscayne mb-3">
        {label}
      </div>
      <p className="text-base text-db-text-1 leading-relaxed whitespace-pre-wrap">{body}</p>
    </div>
  );
}
